import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertColor, Link } from '@mui/material';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import { sendEmailVerification, getAuth } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { sendEmailVerificationProfessional } from '../../graphqlService';
import { findRateLimitMessage, rateLimitMessageFromError } from '../../utils/rateLimitMessage';
import AuthLayout from './AuthLayout';
import AuthStatus from './AuthStatus';
import { describeAuthError, getAuthErrorCode } from './authErrors';

interface Feedback {
  severity: AlertColor;
  message: string;
}

const SIGNED_OUT_MESSAGE = "You're signed out. Sign in again to continue.";

const EmailVerification: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  // The result of the last action on this page, always with an explicit
  // severity. It used to be guessed from the text (an emoji or
  // "successfully"), which broke whenever the wording changed.
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  // Cleared on unmount so a redirect can't fire after the page is gone.
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  // Signup routes here after creating the account, and passes whether the
  // verification email actually went out — it can fail (rate limit, provider
  // outage) while the account itself is created fine.
  const navState = (location.state || {}) as {
    email?: string;
    emailSent?: boolean;
    message?: string;
  };
  const emailFailedToSend = navState.emailSent === false;

  // AuthContext deliberately reports user=null until the email is verified,
  // so `user?.email` was always blank on exactly the page that needs it.
  // Read the live Firebase user instead.
  const pendingEmail = user?.email || getAuth().currentUser?.email || navState.email || '';

  useEffect(() => {
    // If user is already verified, redirect to dashboard
    if (user && user.emailVerified) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleResendEmail = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setFeedback({ severity: 'warning', message: SIGNED_OUT_MESSAGE });
      return;
    }

    setSending(true);
    setFeedback(null);
    try {
      // The backend mints the HMAC-signed verification token server-side;
      // the client-supplied token is unused. It also enforces the email
      // rate limits, which is why it goes first.
      let result: { success?: boolean | null; error?: string | null; message?: string | null } | null = null;
      let backendError: unknown = null;
      try {
        result = await sendEmailVerificationProfessional(currentUser.email!, '');
      } catch (professionalEmailError) {
        backendError = professionalEmailError;
      }

      // The limit answers "Too many emails sent to this address..." — only
      // "Rate limit" used to be recognised, so a real limit fell through to
      // Firebase and the email went out anyway.
      const rateLimited = backendError
        ? rateLimitMessageFromError(backendError)
        : findRateLimitMessage(result?.error, result?.message);
      if (rateLimited) {
        setFeedback({ severity: 'error', message: rateLimited });
        return;
      }

      if (result?.success) {
        setFeedback({ severity: 'success', message: 'Verification email sent. Check your inbox and spam folder.' });
        return;
      }

      // The backend is unreachable, errored, or reported that it couldn't
      // deliver the email (a provider failure, or an account check such as a
      // missing session token). Firebase can still send the signed-in user's
      // own verification email, and has its own rate limit.
      console.warn('Professional email service failed, falling back to Firebase:', backendError ?? result?.error);
      await sendEmailVerification(currentUser);
      setFeedback({ severity: 'success', message: 'Verification email sent. Check your inbox and spam folder.' });
    } catch (error: unknown) {
      setFeedback({
        severity: 'error',
        message: getAuthErrorCode(error)
          ? describeAuthError(error, 'email-verification').message
          : "We couldn't send the verification email. Try again in a few minutes.",
      });
    } finally {
      setSending(false);
    }
  };

  const handleCheckVerification = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setFeedback({ severity: 'warning', message: SIGNED_OUT_MESSAGE });
      return;
    }

    setChecking(true);
    setFeedback(null);
    try {
      await currentUser.reload();
      if (currentUser.emailVerified) {
        setFeedback({ severity: 'success', message: 'Email verified. Taking you to your dashboard…' });
        redirectTimerRef.current = setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        setFeedback({
          severity: 'info',
          message: "Your email isn't verified yet. Open the link we sent you, then try again.",
        });
      }
    } catch (error: unknown) {
      setFeedback({ severity: 'error', message: "We couldn't check your verification. Try again in a moment." });
    } finally {
      setChecking(false);
    }
  };

  // One message, written once: Signup's hand-off says exactly what happened
  // (sent, slow, or not sent); otherwise say where the link went.
  const description =
    navState.message ||
    (pendingEmail
      ? `We sent a verification link to ${pendingEmail}. Open it to activate your account.`
      : 'We sent you a verification link. Open it to activate your account.');

  const resendAction = {
    label: 'Resend verification email',
    onClick: handleResendEmail,
    loading: sending,
    loadingLabel: 'Sending…',
    disabled: checking,
  };
  const checkAction = {
    label: "I've verified my email",
    onClick: handleCheckVerification,
    loading: checking,
    loadingLabel: 'Checking…',
    disabled: sending,
  };

  return (
    <AuthLayout>
      <AuthStatus
        tone={emailFailedToSend ? 'warning' : 'info'}
        icon={<MarkEmailUnreadOutlinedIcon />}
        title="Verify your email"
        description={description}
        // When the email never went out, sending it is the next step.
        primaryAction={emailFailedToSend ? resendAction : checkAction}
        secondaryAction={emailFailedToSend ? checkAction : resendAction}
        footer={
          <Link component={RouterLink} to="/login" variant="body2" sx={{ fontWeight: 600 }}>
            Back to sign in
          </Link>
        }
      >
        {feedback && (
          <Alert
            severity={feedback.severity}
            role={feedback.severity === 'error' || feedback.severity === 'warning' ? 'alert' : 'status'}
            sx={{ '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' } }}
          >
            {feedback.message}
          </Alert>
        )}
      </AuthStatus>
    </AuthLayout>
  );
};

export default EmailVerification;
